import { useState } from "react";

import toast from "react-hot-toast";
import styled from "styled-components";

import { trackAction } from "~/analytics";
import { ButtonLink } from "~/components/Button";
import { Separator } from "~/components/Elements";
import * as Form from "~/components/Form";
import Loading from "~/components/Loading";
import Modal from "~/components/Modal";
import Spacer, { Axis } from "~/components/Spacer";
import { Title } from "~/components/Text";
import { postTxCancel, postTxBulk } from "~/endpoints/transactions";
import { TransactionCancelRequest, TransactionType } from "~/endpoints/transactions/types";
import { useTfSimpleVaspInfo } from "~/endpoints/trustFramework";
import { TransactionWrap } from "~/hooks/use-transaction";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID/useCurrentViewerVaspDID";
import OutgoingTxDisplay from "~/templates/transactions/components/OutgoingTxDisplay";

const FlexDiv = styled.div`
  display: flex;
  justify-content: center;
`;
const CenteredText = styled.div`
  text-align: center;
`;
const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`;

type CancelTxModalProps = {
  transaction: TransactionWrap;
  onClose: () => void;
};

export default function CancelTxModal({ transaction, onClose }: CancelTxModalProps) {
  const [isBulk, setIsBulk] = useState(false);
  const [optionalNoteText, setOptionalNoteText] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const vaspDID = useCurrentViewerVaspDID();
  const trackProps = vaspDID ? { group_vasp: vaspDID } : undefined;
  const { data: externalVASP, isLoading: loadingVaspInfo } = useTfSimpleVaspInfo(
    !transaction.isIncomplete ? transaction.beneficiaryVASPdid : transaction.originatorVASPdid,
  );

  if (loadingVaspInfo) return <Loading />;

  const cancelTransaction = async () => {
    /*This is the regular flow for our customers */

    if (isBulk) {
      const transactions = await postTxBulk({
        action: "cancel",
        originatorVASPdid: transaction.originatorVASPdid,
        beneficiaryVASPdid: transaction.beneficiaryVASPdid,
        reason: optionalNoteText,
      });
      toast.success(`${transactions.length} transfers canceled and moved to COMPLETED`);
      transactions.forEach(() => trackAction("user-canceled-transaction", trackProps));
    } else {
      const payload: TransactionCancelRequest = {
        id: transaction.id,
        vasp_did: vaspDID,
        reason: optionalNoteText,
      };
      await postTxCancel(payload);
      toast.success("Transfer canceled and moved to COMPLETED");
      trackAction("user-canceled-transaction", trackProps);
    }
  };

  const handleCancelTx = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      if (transaction) {
        await cancelTransaction();
      }
    } catch (err) {
      toast.error(`Oops an unexpected error occurred: ${err} `);
    }
    setIsLoading(false);
    onClose();
  };

  const externalVaspName = externalVASP?.name;

  return (
    <>
      <Modal active onClose={onClose}>
        <Modal.Header>
          <Title as="h1" align="centered">
            {"Are you sure?"}
          </Title>
        </Modal.Header>
        <Modal.Body
          styles={{
            maxWidth: 1000,
          }}
        >
          {isLoading && <Loading />}

          <CenteredText>
            {"Are you sure you want to cancel the Travel Rule data transfer for this transaction?"}
          </CenteredText>

          <Spacer axis={Axis.VERTICAL} size={20} />
          <FlexDiv>
            <OutgoingTxDisplay
              customerLogo={transaction.originatorVASP?.logo}
              customerDisplayText={transaction.originatorDisplay}
              counterpartyLogo={transaction.beneficiaryVASP?.logo}
              counterpartyDisplayText={transaction.beneficiaryDisplay}
              counterPartyVASP={externalVASP}
            />
          </FlexDiv>
          <Spacer axis={Axis.VERTICAL} size={40} />
          <div style={{ fontSize: "12px", fontWeight: "bolder" }}>{"Reason (optional)"}</div>
          <Spacer axis={Axis.VERTICAL} size={10} />
          <textarea
            className="textarea"
            placeholder="Leave a note to document your activity. Remember to enter as much detail as possible. You might be asked for it during the next regulatory audit."
            value={optionalNoteText}
            onChange={(event) => {
              setOptionalNoteText(event.target.value);
            }}
          ></textarea>

          <Separator color="link" weight="light" />
          {externalVASP && transaction.type === TransactionType.TRAVELRULE && (
            <Form.CheckboxLabel>
              <Form.CheckboxInput checked={isBulk} onChange={(e) => setIsBulk(e.target.checked)}></Form.CheckboxInput>
              <Form.CheckboxText>
                Cancel all data transfers {transaction.isIncomplete ? "from" : "to"}{" "}
                <b>{externalVaspName || externalVASP?.did}</b> remaining in the Inbox
              </Form.CheckboxText>
            </Form.CheckboxLabel>
          )}
        </Modal.Body>

        <Modal.Footer>
          <SpaceBetween>
            <ButtonLink onClick={onClose}>{"Take me back"}</ButtonLink>
            <ButtonLink color="danger" onClick={handleCancelTx}>
              Cancel this transfer
            </ButtonLink>
          </SpaceBetween>
        </Modal.Footer>
      </Modal>
    </>
  );
}
